import type {PopoverProps} from 'antd';

import {QuestionCircleOutlined} from '@ant-design/icons';
import {useResponsive} from 'ahooks';
import {Popover} from 'antd';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'motion/react';
import React, {forwardRef, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

export interface CustomPopoverProps extends Omit<PopoverProps, 'content'> {
	/** Popover content */
	content?: React.ReactNode
	/** Main content (trigger) */
	children: React.ReactNode
	/** Popover variant */
	variant?: 'default' | 'info' | 'warning' | 'danger' | 'success'
	/** Whether to enable animation */
	animated?: boolean
	/** Custom icon */
	icon?: React.ReactNode
	/** Custom classes for overlay */
	overlayClassName?: string
	/** Show arrow */
	showArrow?: boolean
	/** Arrow position */
	arrowPosition?: 'start' | 'center' | 'end'
	/** Container styles like Ant Design Popover */
	containerStyle?: React.CSSProperties
}

const CustomPopover = forwardRef<
	any, // Changed from HTMLDivElement to any, or use antd's Popover type
	CustomPopoverProps
>(
	(
		{
			content,
			children,
			animated = true,
			icon,
			overlayClassName,
			showArrow = true,
			arrowPosition = 'center',
			containerStyle,
			className,
			style,
			placement = 'top',
			trigger = 'hover',
			...restProps
		},
		ref,
	) => {
		const responsive = useResponsive();

		// Enriched content with icon
		const enrichedContent = useMemo(() => {
			if (!content)
				return null;

			const contentElement = (
				<div
					className={clsx(
						'',
						!responsive.md && 'w-full',
					)}
				>
					<div className="flex items-start gap-2">
						{icon && (
							<div className="flex-shrink-0 mt-0.5">
								{icon}
							</div>
						)}
						<div className="flex-1">
							{typeof content === 'string'
								? (
									<p className="m-0">
{content}
         </p>
								)
								: (
									content
								)}
						</div>
					</div>
				</div>
			);

			return animated
				? (
					<motion.div
						initial={{opacity: 0, scale: 0.95, y: -5}}
						animate={{opacity: 1, scale: 1, y: 0}}
						exit={{opacity: 0, scale: 0.95, y: -5}}
						transition={{duration: 0.15, ease: 'easeOut'}}
					>
						{contentElement}
					</motion.div>
				)
				: (
					contentElement
				);
		}, [content, responsive.md, icon, animated]);

		// Arrow classes based on position
		const arrowClasses = useMemo(() => {
			const positionClasses = {
				start: 'before:left-4',
				center: 'before:left-1/2 before:-translate-x-1/2',
				end: 'before:right-4',
			};
			return positionClasses[arrowPosition];
		}, [arrowPosition]);

		// Final overlay style
		const finalOverlayClassName = useMemo(() => {
			return twMerge(
				clsx(
					'custom-popover-overlay',
					!showArrow && 'ant-popover-no-arrow',
					showArrow && arrowClasses,
					overlayClassName,
				),
			);
		}, [showArrow, arrowClasses, overlayClassName]);

		// Final trigger classes
		const finalClassName = useMemo(() => {
			return twMerge(
				clsx(
					'inline-flex items-center',
					className,
				),
			);
		}, [className]);
		// Merge styles for Popover
		const popoverStyles = useMemo(() => {
			const baseStyles = restProps.styles || {};

			// If containerStyle is provided, merge it with existing styles
			if (containerStyle) {
				return {
					...baseStyles,
					container: containerStyle,
				};
			}

			return baseStyles;
		}, [containerStyle, restProps.styles]);

		return (
			<AnimatePresence>
				<Popover
					ref={ref} // This ref is passed to antd's Popover
					content={enrichedContent}
					overlayClassName={finalOverlayClassName}
					className={finalClassName}
					styles={popoverStyles}
					placement={placement}
					trigger={trigger}
					arrow={showArrow ? {pointAtCenter: true} : false}
					{...restProps}
				>
					<div className="inline-flex items-center cursor-pointer">
						{children}
					</div>
				</Popover>
			</AnimatePresence>
		);
	},
);

CustomPopover.displayName = 'CustomPopover';

// Component with default icon
export const InfoPopover: React.FC<Omit<CustomPopoverProps, 'icon' | 'variant'>> = props => (
	<CustomPopover
		icon={<QuestionCircleOutlined style={{color: '#1677ff'}} />}
		variant="info"
		{...props}
	/>
);

// Helper component for quick usage
export function createPopover(defaultProps: Partial<CustomPopoverProps>) {
	const Component = forwardRef<any, CustomPopoverProps>((props, ref) => (
		<CustomPopover ref={ref} {...defaultProps} {...props} />
	));
	Component.displayName = `CustomPopover(${JSON.stringify(defaultProps)})`;
	return Component;
}

// HOC for adding Popover to other components
export function withPopover<P extends object>(
	WrappedComponent: React.ComponentType<P>,
	popoverProps: Partial<CustomPopoverProps>,
) {
	const ComponentWithPopover: React.FC<P> = props => (
		<CustomPopover {...popoverProps}>
			<WrappedComponent {...props} />
		</CustomPopover>
	);
	ComponentWithPopover.displayName = `withPopover(${WrappedComponent.displayName || WrappedComponent.name})`;
	return ComponentWithPopover;
}

CustomPopover.displayName = "CustomPopover";

export default CustomPopover;
